import {
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  Output,
  EventEmitter,
  ViewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { FieldType } from '@app/core/interfaces/table.interface';
import { Ag1TableService } from '../../ag1-table.service';
import { map, switchMap, take } from 'rxjs/operators';
import { NgTemplateOutlet, NgPlural, NgPluralCase } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1LoaderComponent } from '../../../ag1-loader/ag1-loader.component';
import { Ag1ButtonSmallComponent } from '../../../ag1-button-small/ag1-button-small.component';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { SentenceCasePipe } from '../../../../pipes/sentence.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface IColumnDragState {
  draggingColumnIndex: number | null;
  draggedOverColumnIndex: number | null;
}

interface IColumnResizeState {
  isResizing: boolean;
  columnName: string | null;
  lastUpdate: number;
}

export interface IData {
  ungroupedData: IFlatData[];
  groupedData: IGroupedData[];
}

export interface IFlatData {
  id: number | string; // data must have an id
  [key: string]: any;
}

export interface IGroupedData {
  groupHeader: string;
  groupData: IFlatData[];
  groupFields: string[];
  expanded: boolean;
  count: number;
}

interface IRowExpansionState {
  expandedRowIndex: number | null;
  expandedRowGroupHeader: string | null;
}

interface IRowMergeConfig {
  fieldToMerge: string;
  mergeCriteria: (previousRow: IFlatData, currentRow: IFlatData) => boolean;
}

export interface IMergingConfig {
  rowMergingConfigs?: IRowMergeConfig[];
  // columnMergingConfigs?: IColumnMergeConfig[]; // future feature
}

@Component({
  selector: 'ag1-table-base-old',
  templateUrl: './ag1-table-base-old.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    AngularSvgIconModule,
    Ag1LoaderComponent,
    Ag1ButtonSmallComponent,
    Bind,
    Button,
    Tooltip,
    NgPlural,
    NgPluralCase,
    HumanizePipe,
    SentenceCasePipe,
  ],
})
export class Ag1TableBaseOldComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  // state
  fields: FieldType[];
  isLoading: boolean;
  data: IData = {
    ungroupedData: [],
    groupedData: [],
  };

  /** (Optional) Disable the loading animations on the table */
  @Input() hasLoadingAnimation: boolean = true;
  // (Optional) Disable the border around the table. Useful if the table is embedded inside a view
  @Input() hasBorder: boolean = true;

  // column ordering
  columnDragState: IColumnDragState = {
    draggingColumnIndex: null,
    draggedOverColumnIndex: null,
  };

  // column resizing
  columnResizeState: IColumnResizeState = {
    isResizing: false,
    columnName: null,
    lastUpdate: 0,
  };

  // column sorting
  /** (Required) Event triggered when sorting a column. The purpose of this is to allow a feature to trigger sorting (locally or serverside) */
  @Output() columnSortEvent = new EventEmitter<FieldType>();

  // nested templates
  /** (Optional) Pass in a template that'll be embedded in a nested row. This can be used to nest tables inside of Ag1TableBase */
  @Input() nestedRowRef?: TemplateRef<any>;
  /** (Optional) Event triggered when expanding a nested row. The purpose of this is to allow a feature to capture which row was expanded in order to fetch new data for the nested table */
  @Output() rowExpansionEvent = new EventEmitter<IFlatData>();
  rowExpansionState: IRowExpansionState = {
    expandedRowIndex: null,
    expandedRowGroupHeader: null,
  };

  // custom templates
  /** Pass in a template to customize a header cell */
  @Input() customHeaderCellRef?: TemplateRef<any>;
  /** Pass in a template to customize a body cell */
  @Input() customBodyCellRef?: TemplateRef<any>;
  /** Pass in a template to customize the action header cell */
  @Input() customHeaderActionRef?: TemplateRef<any>;
  /** Pass in a template to customize the action body cell */
  @Input() customBodyActionRef?: TemplateRef<any>;

  // data handling
  /** (Required) Service that contains the state of the Ag1TableBase components */
  @Input() tableService: Ag1TableService;
  /** (Required) Name of the table. This is used for data downloading */
  @Input() tableName: string;

  // Future feature, need to give more thought into the ux of multi column sorting
  // For now it's disabled but can easily be enabled by setting this to true via some condition
  allowMultiColumnSort: boolean = false;

  // cell merging
  /** (Optional) Configuration for merging cells together */
  @Input() mergingConfig: IMergingConfig;

  // grouping
  expandedGroups: Set<string> = new Set<string>();

  // selections
  selectedRows: Set<number | string> = new Set<number | string>();
  selectedGroups: Set<string> = new Set<string>();
  @ViewChild('headerRow', { static: false }) headerRow: any;
  /** Pass in a template to add bulk actions, this activates row selections */
  @Input() customHeaderBulkActionRef?: TemplateRef<any>;
  /** (Optional) Event triggered when a row is selected */
  @Output() rowSelectionEvent = new EventEmitter<(string | number)[]>();
  /** (Optional) Rows that should be excluded from selection */
  @Input() excludedRowsFromSelection: (string | number)[] = [];
  @Input() hasSelectionPermission: boolean = true;

  constructor() {}

  ngOnInit(): void {
    if (!this.tableService) {
      console.error('Table Service is required');
      return;
    }

    if (!this.tableName) {
      console.error('Table Name is required');
      return;
    }

    this.tableService.isLoading$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap((isLoading: boolean) => {
          this.isLoading = isLoading;

          // start listening to data$ observable when isLoading$ emits a value
          return this.tableService.data$.pipe(
            takeUntilDestroyed(this._destroyRef),
            switchMap((data: any[]) => {
              // update the data
              this.data.ungroupedData = data;

              // start listening to fields$ observable when data$ emits a value
              return this.tableService.fields$.pipe(
                takeUntilDestroyed(this._destroyRef),
                map((fields: FieldType[]) => {
                  // update the fields
                  this.fields = fields;
                  // regroup the data
                  this.data.groupedData = this.groupedData;

                  this.refreshInterface();
                }),
              );
            }),
          );
        }),
      )
      .subscribe();
  }

  // events
  toggleNestedRow(
    item: IFlatData,
    rowIndex: number,
    groupHeader: string,
  ): void {
    if (this.rowExpansionState.expandedRowIndex === rowIndex) {
      this.collapseNestedRow();
    } else {
      this.expandNestedRow(item, rowIndex, groupHeader);
    }

    this.refreshInterface();
  }

  collapseNestedRow(): void {
    this.rowExpansionState = {
      expandedRowIndex: null,
      expandedRowGroupHeader: null,
    };
  }

  expandNestedRow(
    item: IFlatData,
    rowIndex: number,
    groupHeader: string,
  ): void {
    this.rowExpansionState = {
      expandedRowIndex: rowIndex,
      expandedRowGroupHeader: groupHeader,
    };

    this.rowExpansionEvent.emit(item); // tell parent row was expanded
  }

  expandGroup(group: IGroupedData): void {
    group.expanded = true;
    this.expandedGroups.add(group.groupHeader);
  }

  collapseGroup(group: IGroupedData): void {
    group.expanded = false;
    this.expandedGroups.delete(group.groupHeader);
  }

  expandAllGroups(): void {
    this.data.groupedData.forEach((group: IGroupedData) => {
      this.expandGroup(group);
    });
  }

  collapseAllGroups(): void {
    this.data.groupedData.forEach((group: IGroupedData) => {
      this.collapseGroup(group);
    });
  }

  selectAllRows(): void {
    const selectedRows = this.data.ungroupedData.filter(
      (row: IFlatData) => !this.excludedRowsFromSelection.includes(row.id),
    );

    // prevent selecting an empty table/ groups
    if (selectedRows.length === 0) {
      return;
    }

    selectedRows.forEach((row: IFlatData) => this.selectedRows.add(row.id));

    this.data.groupedData.forEach((group) =>
      this.selectedGroups.add(group.groupHeader),
    );
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectAllRows(): void {
    this.selectedRows.clear();
    this.selectedGroups.clear();
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  selectGroup(group: IGroupedData): void {
    const rowsToSelect = group.groupData.filter(
      (row: IFlatData) => !this.excludedRowsFromSelection.includes(row.id),
    );

    // prevent selecting an empty group
    if (rowsToSelect.length === 0) {
      return;
    }

    this.selectedGroups.add(group.groupHeader);

    rowsToSelect.forEach((row) => this.selectedRows.add(row.id));
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectGroup(group: IGroupedData): void {
    this.selectedGroups.delete(group.groupHeader);
    group.groupData.forEach((row) => this.selectedRows.delete(row.id));
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  selectRow(row: IFlatData): void {
    if (this.excludedRowsFromSelection.includes(row.id)) {
      return;
    }

    this.selectedRows.add(row.id);
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  deselectRow(row: IFlatData): void {
    this.selectedRows.delete(row.id);
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
  }

  // getters
  get bulkActionContainerWidth(): string {
    if (!this.headerRow) {
      return '0px';
    }

    // bulk actions only start from the second cell
    // so we need to subtract the width of the first cell to prevent overflow
    const firstCell = this.headerRow.nativeElement.firstElementChild;

    return `${
      this.headerRow.nativeElement.offsetWidth - firstCell.offsetWidth
    }px`;
  }

  get isAllGroupsExpanded(): boolean {
    return this.data.groupedData.every((group: IGroupedData) => group.expanded);
  }

  get checkedFields(): FieldType[] {
    return this.fields.filter((field) => field.checked);
  }

  get fieldKeys(): string[] {
    return this.fields.map((field) => field.name);
  }

  get groupedFields(): FieldType[] {
    return this.fields
      .filter((field) => field.grouped && field.groupable)
      .sort((a, b) => {
        if (a.groupOrder === null) return 1;
        if (b.groupOrder === null) return -1;
        return a.groupOrder - b.groupOrder;
      });
  }

  get groupedFieldKeys(): string[] {
    return this.groupedFields.map((field) => field.name);
  }

  get groupedData(): IGroupedData[] {
    if (!this.data?.ungroupedData?.length) {
      return [];
    }

    const groupedData: IGroupedData[] = [];
    this.data.ungroupedData.forEach((item) => {
      // groupHeader
      const groupHeader = this.groupedFieldKeys
        .map((field) => item[field])
        .join(' | ');

      // groupData
      const groupIndex = groupedData.find(
        (group) => group.groupHeader === groupHeader,
      );
      if (groupIndex) {
        groupIndex.groupData.push(item);
        groupIndex.count++;
      } else {
        groupedData.push({
          groupHeader,
          groupData: [item],
          groupFields: this.groupedFieldKeys,
          expanded: true,
          count: 1,
        });
      }
    });

    return groupedData;
  }

  get hasGroupedData(): boolean {
    return !!this.groupedFields.length;
  }

  get hasNestedRow(): boolean {
    return !!this.nestedRowRef;
  }

  get hasCustomAction(): boolean {
    return !!this.customBodyActionRef || !!this.customHeaderActionRef;
  }

  get hasSelectableData(): boolean {
    return !!this.customHeaderBulkActionRef && this.hasSelectionPermission;
  }

  get isAllSelected(): boolean {
    return this.selectedRows.size === this.data.ungroupedData.length;
  }

  get isAnySelected(): boolean {
    return (
      this.selectedRows.size < this.data.ungroupedData.length &&
      this.selectedRows.size > 0
    );
  }

  resetSelectedRows(): void {
    this.selectedRows.clear();
    this.selectedGroups.clear();
    this.cdr.detectChanges();
  }

  selectRowById(id: number | string): void {
    if (this.excludedRowsFromSelection.includes(id)) {
      return;
    }
    this.selectedRows.add(id);
    this.rowSelectionEvent.emit(Array.from(this.selectedRows));
    this.cdr.detectChanges();
  }

  // trackBys
  trackByGroup(_index: number, group: IGroupedData): string {
    return group.groupHeader;
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  // column sorting
  updateColumnSortDirection(field: FieldType) {
    if (!field?.sortable) {
      return;
    }

    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) => {
            if (f.name === field.name) {
              return {
                ...f,
                sortDirection: this.tableService.sortDirection(f),
              };
            }

            return f;
          });

          // Get the sorted non-grouped fields
          const sortedNonGroupedFields = updatedFields.filter(
            (f) => !f.grouped && f.sortDirection,
          );

          if (sortedNonGroupedFields.length > 0) {
            // If there are sorted non-grouped fields, remove sortDirection from grouped fields
            updatedFields.forEach((f) => {
              if (f.grouped) {
                f.sortDirection = null;
              }

              if (
                f.name !== field.name &&
                f.sortDirection &&
                !this.allowMultiColumnSort
              ) {
                f.sortDirection = null;
              }
            });
          } else {
            // If there are no sorted non-grouped fields, sort the grouped fields
            updatedFields.forEach((f) => {
              if (f.grouped && f.sortDirection === null) {
                f.sortDirection = this.tableService.sortDirection(f);
              }
            });
          }

          // console.log('updatedFields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe((fields) => {
        this.tableService.updateFields(fields);
        this.columnSortEvent.emit(field);
      });
  }

  // column ordering
  onColumnDragStart(event: DragEvent, index: number): void {
    this.columnDragState.draggingColumnIndex = index;
    // set dataTransfer to allow drop
    event.dataTransfer?.setData('text/plain', '');
  }

  onColumnDragOver(event: DragEvent, index: number): void {
    event.preventDefault();

    // show drop location
    this.columnDragState.draggedOverColumnIndex = index;
  }

  onColumnDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();

    // check that drop location is valid
    if (
      this.columnDragState.draggingColumnIndex === null ||
      this.columnDragState.draggingColumnIndex === dropIndex
    ) {
      console.warn('invalid drop');
      this.resetDragState();
      return;
    }

    this.updateColumnOrder(
      this.fields[this.columnDragState.draggingColumnIndex],
      dropIndex,
    );
  }

  updateColumnOrder(field: FieldType, newIndex: number): void {
    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const currentIndex = fields.findIndex(
            (f: FieldType) => f.name === field.name,
          );

          const updatedFields: FieldType[] = [...fields];
          updatedFields.splice(currentIndex, 1);
          updatedFields.splice(newIndex, 0, field);
          updatedFields.forEach((f, index) => (f.order = index));

          // console.log('ordered fields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe((fields) => {
        this.resetDragState();
        this.tableService.updateFields(fields);
      });
  }

  resetDragState(): void {
    this.columnDragState = {
      draggingColumnIndex: null,
      draggedOverColumnIndex: null,
    };
  }

  // column resizing
  onColumnResizeMouseDown(event: MouseEvent, field: FieldType): void {
    event.preventDefault();

    // If the field does not have an initial width,get the parent element width.
    // This prevents the column from resizing to a very small width on initial resize.
    let parentWidth;
    if (!field?.width) {
      const parentElement = (event.target as Element).closest('th');
      // Initial parent width would be based on the content of the cell
      parentWidth = parentElement?.getBoundingClientRect().width;
    }

    this.columnResizeState.columnName = field.name;
    const initialX = event.clientX;
    const initialWidth = field?.width || parentWidth;
    let newWidth = initialWidth;

    // check if the mouse is moved while pressed
    const onMouseMove = (moveEvent: MouseEvent) => {
      const currentTime = Date.now();

      const widthChange = moveEvent.clientX - initialX;
      newWidth = initialWidth + widthChange;

      // only check for width updates every interval
      // this is for performance reasons
      const widthUpdateInterval = 50;
      if (
        currentTime - this.columnResizeState.lastUpdate >=
        widthUpdateInterval
      ) {
        field.width = newWidth;
        this.columnResizeState.lastUpdate = currentTime;
        this.columnResizeState.isResizing = true;
        this.refreshInterface();
      }
    };

    // check if the mouse is released
    const onMouseUp = (): void => {
      // remove current listeners to complete the resize
      // and prevent memory leaks
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);

      field.width = newWidth;
      this.updateColumnWidth(field);
    };

    // Start listening to new mouse movements
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }

  updateColumnWidth(field: FieldType): void {
    this.tableService.fields$
      .pipe(
        take(1),
        map((fields: FieldType[]) => {
          const updatedFields: FieldType[] = fields.map((f: FieldType) =>
            f.name === field.name ? { ...f, width: f.width } : f,
          );

          // console.log('resized fields', updatedFields);
          return updatedFields;
        }),
      )
      .subscribe((fields) => {
        this.resetColumnResizeState();
        this.tableService.updateFields(fields);
      });
  }

  resetColumnResizeState(): void {
    this.columnResizeState = {
      isResizing: false,
      columnName: null,
      lastUpdate: 0,
    };
  }

  // cell merging
  isRowCellMerged(
    currentRow: IFlatData,
    rowIndex: number,
    data: IFlatData[],
    currentField: FieldType,
  ): boolean {
    if (!this.mergingConfig?.rowMergingConfigs) {
      return false;
    }

    const previousRow = data[rowIndex - 1];
    return (
      previousRow &&
      this.mergingConfig.rowMergingConfigs.some(
        (config) =>
          config.fieldToMerge === currentField.name &&
          config.mergeCriteria(previousRow, currentRow),
      )
    );
  }

  getMergedRowspan(
    currentRowIndex: number,
    data: IFlatData[],
    currentField: FieldType,
  ): number {
    if (!this.mergingConfig?.rowMergingConfigs) {
      return 1;
    }

    let rowspan = 1;
    let nextRowIndex = currentRowIndex + 1;

    while (
      data.length > nextRowIndex &&
      this.isRowCellMerged(data[nextRowIndex], nextRowIndex, data, currentField)
    ) {
      rowspan++;
      nextRowIndex++;
    }

    return rowspan;
  }
}
